// packages/client/src/game/ads/AirplaneRenderer.ts
import * as THREE from "three";
import { AIRPLANE_CONFIG } from "../config";
import { AirplaneEnvironmentElement } from "../../../../shared/dist/environment.types.js";

export class AirplaneRenderer {
  private scene: THREE.Scene;
  private airplanes: THREE.Group[] = [];

  constructor(scene: THREE.Scene) {
    this.scene = scene;
    console.log("[AirplaneRenderer] Zainicjalizowano renderer samolotów");

    // Inicjalizacja domyślnych samolotów
    //this.initDefaultAirplanes();
  }

  /**
   * Inicjalizuje domyślne samoloty w środowisku
   */
  /*
  private initDefaultAirplanes(): void {
    // Tworzymy przykładowy samolot z banerem
    const airplane1 = this.createAirplaneWithBanner("WELCOME TO TANKS!", 0x0000ff);
    airplane1.position.set(-200, 50, -50);
    this.scene.add(airplane1);
    this.airplanes.push(airplane1);

    // Tworzymy drugi samolot latający w przeciwnym kierunku
    const airplane2 = this.createAirplaneWithBanner("DESTROY YOUR ENEMIES!", 0xff0000);
    airplane2.position.set(200, 60, 100);
    airplane2.rotation.y = Math.PI; // Obracamy o 180 stopni
    this.scene.add(airplane2);
    this.airplanes.push(airplane2);
  }
    */

  /**
   * Tworzy model samolotu z transparentem
   * @param message Wiadomość na transparencie
   * @param textColor Kolor tekstu
   * @returns Grupa Three.js zawierająca model samolotu z transparentem
   */
  public createAirplaneWithBanner(
    message: string,
    textColor: number = 0xff0000
  ): THREE.Group {
    const airplaneGroup = new THREE.Group();
    airplaneGroup.name = "airplane_with_banner";

    // Model samolotu
    const airplane = this.createAirplaneModel();
    airplaneGroup.add(airplane);

    // Transparent
    const banner = this.createBanner(message, textColor);
    banner.position.set(-25, 0, 0); // Pozycja za samolotem
    airplaneGroup.add(banner);

    // Dodajemy właściwości do animacji
    (airplaneGroup as any).direction =
      airplaneGroup.rotation.y === Math.PI ? -1 : 1; // Kierunek lotu
    (airplaneGroup as any).speed = AIRPLANE_CONFIG.SPEED;
    (airplaneGroup as any).originalY = airplaneGroup.position.y;

    return airplaneGroup;
  }

  /**
   * Tworzy uproszczony model samolotu
   */
  private createAirplaneModel(): THREE.Group {
    const airplaneModel = new THREE.Group();
    airplaneModel.name = "airplane_model";

    // Korpus samolotu
    const bodyGeometry = new THREE.CylinderGeometry(0.5, 0.5, 5, 8);
    bodyGeometry.rotateZ(Math.PI / 2); // Korpus wzdłuż osi X
    const bodyMaterial = new THREE.MeshPhongMaterial({
      color: AIRPLANE_CONFIG.COLORS.AIRPLANE_BODY,
    });
    const body = new THREE.Mesh(bodyGeometry, bodyMaterial);
    airplaneModel.add(body);

    // Skrzydła
    const wingGeometry = new THREE.BoxGeometry(0.2, 1, 7);
    const wingMaterial = new THREE.MeshPhongMaterial({
      color: AIRPLANE_CONFIG.COLORS.AIRPLANE_WINGS,
    });
    const wing = new THREE.Mesh(wingGeometry, wingMaterial);
    airplaneModel.add(wing);

    // Ogon
    const tailGeometry = new THREE.BoxGeometry(0.2, 2, 1);
    tailGeometry.rotateZ(Math.PI / 4);
    const tailMaterial = new THREE.MeshPhongMaterial({
      color: AIRPLANE_CONFIG.COLORS.AIRPLANE_WINGS,
    });
    const tail = new THREE.Mesh(tailGeometry, tailMaterial);
    tail.position.set(-2, 0, 0);
    airplaneModel.add(tail);

    // Rzucaj cień
    airplaneModel.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        child.castShadow = true;
      }
    });

    return airplaneModel;
  }

  /**
   * Tworzy transparent z tekstem
   */
  private createBanner(
    message: string,
    textColor: number = 0xff0000
  ): THREE.Group {
    const bannerGroup = new THREE.Group();
    bannerGroup.name = "banner";

    // Tworzymy canvas dla tekstu
    const canvas = document.createElement("canvas");
    const context = canvas.getContext("2d");
    const bannerWidth = AIRPLANE_CONFIG.BANNER_WIDTH * 20; // Wyższa rozdzielczość dla tekstu
    const bannerHeight = AIRPLANE_CONFIG.BANNER_HEIGHT * 20;
    canvas.width = bannerWidth;
    canvas.height = bannerHeight;

    if (context) {
      // Wypełniamy tło
      context.fillStyle = "#FFFFFF";
      context.fillRect(0, 0, bannerWidth, bannerHeight);

      // Rysujemy ramkę
      context.strokeStyle = "#000000";
      context.lineWidth = 8;
      context.strokeRect(4, 4, bannerWidth - 8, bannerHeight - 8);

      // Rysujemy tekst
      const fontSize = 40;
      context.font = `bold ${fontSize}px Arial`;
      context.fillStyle = `#${textColor.toString(16).padStart(6, "0")}`;
      context.textAlign = "center";
      context.textBaseline = "middle";
      context.fillText(message, bannerWidth / 2, bannerHeight / 2);
    }

    // Tworzymy teksturę i materiał
    const texture = new THREE.CanvasTexture(canvas);
    const material = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      transparent: true,
    });

    // Tworzymy mesh transparentu
    const bannerGeometry = new THREE.PlaneGeometry(
      AIRPLANE_CONFIG.BANNER_WIDTH,
      AIRPLANE_CONFIG.BANNER_HEIGHT
    );
    const bannerMesh = new THREE.Mesh(bannerGeometry, material);
    bannerGroup.add(bannerMesh);

    // Tył transparentu - ta sama tekstura (wcześniej drugi CanvasTexture z tego
    // samego canvas = podwójny upload VRAM za identyczny obraz).
    const materialBack = new THREE.MeshBasicMaterial({
      map: texture,
      side: THREE.DoubleSide,
      transparent: true,
    });
    const bannerMeshBack = new THREE.Mesh(bannerGeometry, materialBack);
    bannerMeshBack.position.set(0, 0, -0.01); // Minimalnie z tyłu
    bannerMeshBack.rotation.y = Math.PI; // Odwróć o 180 stopni
    bannerGroup.add(bannerMeshBack);

    // Dodajemy liny łączące z samolotem
    const ropeLength = 15; // Długość lin
    const ropeGeometry = new THREE.CylinderGeometry(0.05, 0.05, ropeLength, 8);
    // Obracamy liny tak, aby łączyły samolot z banerem
    ropeGeometry.rotateZ(Math.PI / 2);

    const ropeMaterial = new THREE.MeshBasicMaterial({
      color: AIRPLANE_CONFIG.COLORS.ROPES,
    });

   // Punkty zaczepienia
   const airplaneAttachmentPoint = new THREE.Vector3(-0.5, 0, 0); // Tył samolotu
   const bannerLeftAttachment = new THREE.Vector3(0, 0, -AIRPLANE_CONFIG.BANNER_WIDTH / 2); // Lewy róg banera
   //const bannerRightAttachment = new THREE.Vector3(10, 0, AIRPLANE_CONFIG.BANNER_WIDTH / 2); // Prawy róg banera
 
   // Wektory i długości lin
   /*
   const leftRopeVector = bannerLeftAttachment.clone().sub(airplaneAttachmentPoint);
   //const rightRopeVector = bannerRightAttachment.clone().sub(airplaneAttachmentPoint);
   const leftRopeLength = leftRopeVector.length();
   //const rightRopeLength = rightRopeVector.length();
 
   // Geometria lin
   const ropeGeometryLeft = new THREE.CylinderGeometry(0.05, 0.05, leftRopeLength, 8);
   //const ropeGeometryRight = new THREE.CylinderGeometry(0.05, 0.05, rightRopeLength, 18);
 
   // Tworzenie lin
   const ropeLeft = new THREE.Mesh(ropeGeometryLeft, ropeMaterial);
   //const ropeRight = new THREE.Mesh(ropeGeometryRight, ropeMaterial);
 
   // Pozycja lin (w połowie drogi)
   ropeLeft.position.copy(airplaneAttachmentPoint.clone().add(leftRopeVector.clone().multiplyScalar(0.5)));
   //ropeRight.position.copy(airplaneAttachmentPoint.clone().add(rightRopeVector.clone().multiplyScalar(0.5)));
 
   // Rotacja lin
   //ropeLeft.quaternion.setFromUnitVectors(new THREE.Vector3(0, 0, 0), leftRopeVector.clone().normalize());
   //ropeRight.quaternion.setFromUnitVectors(new THREE.Vector3(30, 30, 30), rightRopeVector.clone().normalize());
 

    bannerGroup.add(ropeLeft);
    //bannerGroup.add(ropeRight);
    */
    bannerGroup.add(bannerMesh);

    return bannerGroup;
  }

  /**
   * Aktualizuje pozycje samolotów, dodając efekt falowania
   * @param deltaTime Czas od ostatniej klatki w sekundach
   */
  public update(): void {
    for (const airplane of this.airplanes) {
      // Pobierz kierunek i prędkość zapisane w obiekcie
      const direction = (airplane as any).direction || 1;
      const speed = (airplane as any).speed || AIRPLANE_CONFIG.SPEED;

      // Aktualizuj pozycję X
      airplane.position.x += direction * speed;

      // Delikatny ruch falujący w pionie z mniejszą amplitudą
      const originalY = (airplane as any).originalY || airplane.position.y;

      // Używamy parametrów z konfiguracji dla amplitudy falowania
      const yOffset =
        Math.sin(Date.now() * 0.001 * AIRPLANE_CONFIG.WAVE_FREQUENCY) *
        AIRPLANE_CONFIG.WAVE_AMPLITUDE;

      // Nowa pozycja Y z ograniczeniem minimum wysokości
      const newY = originalY + yOffset;
      airplane.position.y = Math.max(newY, AIRPLANE_CONFIG.MIN_ALTITUDE);

      // Jeśli samolot wyleci poza mapę, przenieś go na drugi koniec
      if (direction > 0 && airplane.position.x > 300) {
        airplane.position.x = -300;
      } else if (direction < 0 && airplane.position.x < -300) {
        airplane.position.x = 300;
      }
    }
  }

  /**
   * Dodaje samolot na podstawie danych z elementu środowiska
   * @param element Element środowiska zawierający dane samolotu
   */
  public addAirplaneFromElement(element: AirplaneEnvironmentElement): void {
    // Pobierz dane samolotu
    const message = element.data?.message || "NO MESSAGE";
    const color = element.data?.color || "#FF0000";

    // Konwertuj kolor tekstowy na liczbowy (THREE.js używa 0xRRGGBB)
    const textColorValue = parseInt(color.replace("#", ""), 16) || 0xff0000;

    // Stwórz model samolotu
    const airplane = this.createAirplaneWithBanner(message, textColorValue);

    // Ustaw pozycję, rotację i skalę
    airplane.position.copy(element.position);
    airplane.rotation.copy(element.rotation);

    // Ustaw dodatkowe parametry do animacji
    (airplane as any).direction = 1; // Domyślny kierunek
    (airplane as any).speed = element.data?.speed || AIRPLANE_CONFIG.SPEED;
    (airplane as any).originalY = element.position.y;

    // Dodaj do sceny
    this.addAirplane(airplane);
  }

  /**
   * Dodaje samolot do sceny
   * @param airplane Samolot do dodania
   */
  public addAirplane(airplane: THREE.Group): void {
    this.scene.add(airplane);
    this.airplanes.push(airplane);
  }

  public clearAllAirplanes(): void {
    for (const airplane of this.airplanes) {
      this.scene.remove(airplane);
      this.disposeAirplane(airplane);
    }
    this.airplanes.length = 0;
  }

  private disposeAirplane(airplane: THREE.Object3D): void {
    // Shared texture/geometry between front/back banner meshes - dispose once.
    const disposedGeometries = new Set<THREE.BufferGeometry>();
    const disposedMaterials = new Set<THREE.Material>();
    const disposedTextures = new Set<THREE.Texture>();
    airplane.traverse((child) => {
      if (!(child instanceof THREE.Mesh)) {
        return;
      }

      if (child.geometry && !disposedGeometries.has(child.geometry)) {
        disposedGeometries.add(child.geometry);
        child.geometry.dispose();
      }

      const material = child.material;
      const materials = Array.isArray(material) ? material : [material];
      materials.forEach((item) => {
        if (!item || disposedMaterials.has(item)) {
          return;
        }
        disposedMaterials.add(item);
        const map = (item as THREE.MeshBasicMaterial).map;
        if (map && !disposedTextures.has(map)) {
          disposedTextures.add(map);
          map.dispose();
        }
        item.dispose();
      });
    });
  }

  /**
   * Zwraca wszystkie samoloty
   */
  public getAirplanes(): THREE.Group[] {
    return this.airplanes;
  }
}
